import { Checkbox, Label } from '@teach/ui'
import { QuotePlate } from '@/features/ai/components/QuotePlate'
import { addCredits, formatCreditsWithWord } from '@/features/ai/lib/money'
import type { ImageQuoteView } from '@/features/ai/useImageQuote'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  quote: ImageQuoteView
  /** Считает диалог тем же выражением, что гасит запуск в авто-режиме. */
  nothingToDraw: boolean
  /** Заказано наборов пилотов: 0 или 1. Граф рисует один набор за прогон. */
  pilotSets: number
  onPilotSetsChange: (next: number) => void
}

// Согласование слова «вариант» с числом сметы (M7, финальное ревью):
// `pilot_variants` серверный и настраиваемый, и при 1 или 2 без склонения
// получилось бы «1 вариантов» — склоняет `messages.pilotOffer`.

/**
 * Итог АВТО-режима: сколько мест нарисуется и во что это обойдётся. Числа
 * берутся у сервера (`auto_targets`/`auto_cost`) и к ручному режиму отношения
 * не имеют — там план и цену считает `TargetPicker` из выбранных строк,
 * поэтому блок и вынесен отдельно от общих объяснений (`DrawImagesBody`).
 *
 * Тумблер набора пилотов живёт ТОЛЬКО здесь, а не в `DrawImagesModes` или
 * ручном режиме: граф рисует набор для ПЕРВОЙ картинки плана, что в
 * авто-режиме предсказуемо (баннер), а в ручном — как ляжет. Тумблер стоит
 * тут же, потому что он меняет И сумму на кнопке, И тело старта — печатать
 * одно, а слать другое было бы худшим исходом (то же правило записано в
 * `EditorHeader/translation/TranslatePlan.tsx`).
 */
export const AutoPlanSummary = ({ quote, nothingToDraw, pilotSets, onPilotSetsChange }: Props) => {
  // Оба поля сметы приезжают ВМЕСТЕ или не приезжают вовсе — объединяем их в
  // один узкий тип сразу, вместо повторной проверки `!== undefined` у каждого
  // поля по отдельности ниже.
  const m = useMessages(messages)
  const pilotOffer =
    quote.pilotVariants !== undefined && quote.pilotSetCost !== undefined
      ? { variants: quote.pilotVariants, cost: quote.pilotSetCost }
      : null
  // Цена с набором складывается ГОТОВЫМ хелпером на строках: `Number` к
  // деньгам — это округление реального баланса автора. Недоехавшая цена не
  // печатается нулём: ноль обещал бы бесплатный прогон.
  const price =
    quote.autoCost !== undefined && pilotSets > 0 && pilotOffer
      ? addCredits(quote.autoCost, pilotOffer.cost)
      : quote.autoCost

  return (
    <div className="space-y-3 text-sm">
      <p data-testid="draw-plan-size" className="text-foreground">
        {m.toDraw}
        <span className="font-medium">{quote.autoTargets ?? '—'}</span>
      </p>

      {pilotOffer && !nothingToDraw ? (
        // M-5 (финальное ревью): `Checkbox` — Radix, рендерит
        // `button[role="checkbox"]` без содержимого; обёртка `<label>` без
        // `htmlFor` даёт имя нативным полям, но не кастомной ARIA-кнопке —
        // канонический приём каталога (`CourseTargetRow.tsx`) — явная пара
        // `Checkbox id` + `Label htmlFor`.
        <div className="flex items-start gap-2">
          <Checkbox
            id="auto-plan-pilot-sets"
            checked={pilotSets > 0}
            onCheckedChange={(next) => onPilotSetsChange(next ? 1 : 0)}
          />
          <Label htmlFor="auto-plan-pilot-sets" className="text-sm font-normal leading-normal">
            {m.pilotOffer(pilotOffer.variants, formatCreditsWithWord(pilotOffer.cost))}
            <span className="block text-xs text-muted-foreground">{m.pilotPaidAll}</span>
          </Label>
        </div>
      ) : null}

      <QuotePlate price={price} balance={quote.balance} isLoading={quote.isLoading} />
      {nothingToDraw ? <p className="text-xs text-muted-foreground">{m.nothingToDraw}</p> : null}
    </div>
  )
}
